import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, type FbsPostingDetail, HttpError } from '../api';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { useAuth } from '../auth';

interface ScannedMark {
  product_id: number;
  raw: string;
}

export function ShipmentDetailPage() {
  const { postingNumber } = useParams<{ postingNumber: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const posting = useQuery({
    queryKey: ['fbs-posting', postingNumber],
    queryFn: () => api.fbsPostingGet(postingNumber!),
    enabled: !!postingNumber,
  });

  // Локально хранимые отсканированные коды
  const [scanned, setScanned] = useState<ScannedMark[]>([]);
  // Кэшированный выбор активного товара для сканирования (если несколько с маркировкой)
  const [activeProductId, setActiveProductId] = useState<number | null>(null);
  // Ручной ввод марки (для физ-сканера или backup)
  const [manualInput, setManualInput] = useState('');
  // Камера активна?
  const [cameraOn, setCameraOn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleScan = (code: string) => {
    setError(null);
    if (!posting.data) return;
    if (!activeProductId) {
      const candidates = posting.data.products.filter((p) => p.mandatory_mark_required);
      if (candidates.length === 1) setActiveProductId(candidates[0]!.product_id);
      else { setError('Выберите товар для привязки кода'); return; }
    }
    const pid = activeProductId ?? posting.data.products.find((p) => p.mandatory_mark_required)?.product_id;
    if (!pid) return;
    // Дубликат?
    if (scanned.find((s) => s.raw === code)) {
      setError(`Код уже отсканирован`);
      return;
    }
    // Превышение лимита?
    const product = posting.data.products.find((p) => p.product_id === pid);
    if (!product) return;
    const currentCount = scanned.filter((s) => s.product_id === pid).length;
    if (currentCount >= product.quantity) {
      setError(`Для ${product.name} уже отсканировано ${currentCount} из ${product.quantity}`);
      return;
    }
    setScanned((prev) => [...prev, { product_id: pid, raw: code }]);
    setSuccess(`✓ +1 для ${product.name}`);
    setTimeout(() => setSuccess(null), 1500);
    setManualInput('');
  };

  const removeScan = (index: number) => {
    setScanned((prev) => prev.filter((_, i) => i !== index));
  };

  const requiredCount = useMemo(() => {
    if (!posting.data) return 0;
    return posting.data.products.filter((p) => p.mandatory_mark_required).reduce((s, p) => s + p.quantity, 0);
  }, [posting.data]);

  const allRequiredScanned = requiredCount > 0 && scanned.length === requiredCount;
  const hasMarkRequiredItems = (posting.data?.products ?? []).some((p) => p.mandatory_mark_required);

  const ship = useMutation({
    mutationFn: () => {
      const productMap = new Map<number, { product_id: number; quantity: number; exemplars: { mandatory_mark: string }[] }>();
      for (const p of posting.data!.products) {
        productMap.set(p.product_id, { product_id: p.product_id, quantity: p.quantity, exemplars: [] });
      }
      for (const s of scanned) {
        productMap.get(s.product_id)?.exemplars.push({ mandatory_mark: s.raw });
      }
      return api.fbsShip(postingNumber!, { packages: [{ products: Array.from(productMap.values()) }] });
    },
    onSuccess: () => { setTimeout(() => navigate('/shipments'), 1500); },
  });

  if (posting.isLoading) return <div className="card">Загрузка отправления…</div>;
  if (posting.error) return <div className="card" style={{ color: 'var(--red)' }}>{(posting.error as Error).message}</div>;
  if (!posting.data) return null;

  return (
    <>
      <div className="page-header">
        <h2>{posting.data.posting_number}</h2>
        <Link to="/shipments" className="btn">← К списку</Link>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <strong>{posting.data.delivery_method_name}</strong>
            <div className="muted" style={{ fontSize: 12 }}>
              {posting.data.shipment_date && `до ${new Date(posting.data.shipment_date).toLocaleString('ru-RU')}`}
            </div>
          </div>
          <div>
            <a href={`/api/fbs/postings/${postingNumber}/label`} target="_blank" rel="noreferrer" className="btn">📄 Этикетка PDF</a>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Состав</h3>
        <table className="products">
          <thead>
            <tr><th></th><th>Товар</th><th>Артикул</th><th className="num">Кол-во</th><th>Маркировка</th></tr>
          </thead>
          <tbody>
            {posting.data.products.map((p) => {
              const count = scanned.filter((s) => s.product_id === p.product_id).length;
              const isActive = activeProductId === p.product_id;
              const isCompleted = p.mandatory_mark_required && count >= p.quantity;
              return (
                <tr key={p.product_id}>
                  <td style={{ width: 30 }}>
                    {p.mandatory_mark_required && (
                      <input type="radio" name="activeProduct" checked={isActive} onChange={() => setActiveProductId(p.product_id)} />
                    )}
                  </td>
                  <td>{p.name}</td>
                  <td className="muted">{p.offer_id}</td>
                  <td className="num">{p.quantity}</td>
                  <td>
                    {!p.mandatory_mark_required && <span className="muted" style={{ fontSize: 12 }}>не требуется</span>}
                    {p.mandatory_mark_required && (
                      <span style={{ color: isCompleted ? 'var(--green)' : 'var(--text-muted)', fontWeight: isCompleted ? 600 : 'normal' }}>
                        {isCompleted ? '✓' : '⚠'} {count}/{p.quantity}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {hasMarkRequiredItems && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>Сканирование маркировки</h3>
            <button className="btn" onClick={() => setCameraOn((v) => !v)}>{cameraOn ? '📷 Выключить камеру' : '📷 Включить камеру'}</button>
          </div>

          {!activeProductId && <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>Выберите товар (◯ слева в таблице) перед сканированием.</div>}

          {cameraOn && <div style={{ marginBottom: 12 }}><BarcodeScanner onScan={handleScan} /></div>}

          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Или введите вручную / USB-сканером:</div>
            <input
              className="input"
              autoFocus
              placeholder="Скан / ввод кода Data Matrix"
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && manualInput.trim()) handleScan(manualInput.trim()); }}
              style={{ width: '100%', maxWidth: 480 }}
            />
          </div>

          {error && <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 8 }}>{error}</div>}
          {success && <div style={{ color: 'var(--green)', fontSize: 13, marginTop: 8 }}>{success}</div>}

          {scanned.length > 0 && (
            <details style={{ marginTop: 12 }}>
              <summary style={{ cursor: 'pointer', fontSize: 13 }}>Отсканировано: {scanned.length}</summary>
              <ul style={{ fontSize: 12, fontFamily: 'monospace' }}>
                {scanned.map((s, i) => (
                  <li key={i}>
                    {s.raw.slice(0, 60)}{s.raw.length > 60 && '…'}
                    <button className="btn" style={{ marginLeft: 8, padding: '2px 6px' }} onClick={() => removeScan(i)}>×</button>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <div className="card">
        {isAdmin ? (
          <button
            className="btn primary"
            disabled={(hasMarkRequiredItems && !allRequiredScanned) || ship.isPending}
            onClick={() => ship.mutate()}
            style={{ fontSize: 15, padding: '10px 24px' }}
          >
            {ship.isPending ? 'Отгрузка…' : `🚚 Отгрузить${hasMarkRequiredItems ? ` (${scanned.length}/${requiredCount})` : ''}`}
          </button>
        ) : (
          <div className="muted">Только администратор может выполнять отгрузку.</div>
        )}

        {ship.isSuccess && <div style={{ color: 'var(--green)', marginTop: 12 }}>✓ Отгружено! shipment_id: {ship.data.shipment_id}</div>}
        {ship.error && (
          <div style={{ color: 'var(--red)', marginTop: 12 }}>
            {ship.error instanceof HttpError ? `Ошибка ${ship.error.status}: ${JSON.stringify(ship.error.payload)}` : (ship.error as Error).message}
          </div>
        )}
      </div>
    </>
  );
}
